/**
 * Routing der Anwendung.
 *
 * Die Partie liegt unter `/game/:gameId` und teilt sich den Rahmen (Shell), der
 * Seitenleiste, Kopfzeile und Live-Verbindung bereitstellt. Alle Panel-Bereiche
 * sind Unterrouten davon.
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from './components/layout/Shell';
import { EmailReplacementGate } from './components/layout/EmailReplacementGate';
import { useAuth } from './store/auth';
import { Login } from './pages/Login';
import { ForgotPassword, ResetPassword, VerifyEmail } from './pages/Account';
import { GameList } from './pages/GameList';
import { Chat } from './pages/Chat';
import { Leaderboard } from './pages/Leaderboard';
import { Admin } from './pages/Admin';
import { AdminGames } from './pages/AdminGames';
import { AdminGameDetail } from './pages/AdminGameDetail';
import { Dashboard } from './pages/Dashboard';
import { Datacenters, Servers } from './pages/Infrastructure';
import { Bgp, Ipam, Links, Peering, Routers, Switches } from './pages/Network';
import { Customers, Finance, Invoices, Products, StaffPage, Tickets } from './pages/Business';
import { Dedicated, Logs, Monitoring, Services, VpsPage } from './pages/Operations';
import { NetworkMap } from './pages/NetworkMap';
import { Migrations } from './pages/Migration';
import { LoadBalancers } from './pages/LoadBalancer';
import { Advisor, Research, Settings } from './pages/Management';
import { Diagnostics } from './pages/Diagnostics';
import { Objectives } from './pages/Objectives';
import { Chronicle } from './pages/Chronicle';
import { Market, News, WorldPage } from './pages/World';
import { Shop } from './pages/Shop';
import { Zuhause } from './pages/Zuhause';
import { VorOrt } from './pages/VorOrt';
import { Innenraum } from './pages/Innenraum';
import { Offers, Parts } from './pages/Procurement';
import { Help } from './pages/Help';
import { Datenschutz, Impressum, Nutzungsbedingungen } from './pages/Legal';

function RequireAuth({ children }: { children: JSX.Element }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  // Wegwerfadresse aus der Zeit vor der Registrierungsprüfung: erst eine
  // echte Adresse bestätigen, dann weiter zu Partien oder Dashboard.
  if (user.emailNeedsReplacement) return <EmailReplacementGate />;
  return children;
}

function RequireStaff({ children }: { children: JSX.Element }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.platformRole === 'USER') return <Navigate to="/" replace />;
  return children;
}

/**
 * Enger als `RequireStaff`: Zugriff auf fremde Partien und deren simulierte
 * Kunden ist mächtiger als Rollen ändern oder Konten löschen – deshalb bleibt
 * er der obersten Stufe vorbehalten.
 */
function RequireOwner({ children }: { children: JSX.Element }) {
  const user = useAuth((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.platformRole !== 'OWNER') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Ziele der Links aus den Kontomails – ohne Anmeldung erreichbar. */}
      <Route path="/bestaetigen" element={<VerifyEmail />} />
      <Route path="/passwort-vergessen" element={<ForgotPassword />} />
      <Route path="/passwort-neu" element={<ResetPassword />} />

      {/* Rechtstexte ohne Anmeldung erreichbar – ein Impressum hinter dem Login
          erfüllt seinen Zweck nicht. */}
      <Route path="/legal/impressum" element={<Impressum />} />
      <Route path="/legal/datenschutz" element={<Datenschutz />} />
      <Route path="/legal/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
      <Route
        path="/admin"
        element={
          <RequireStaff>
            <Admin />
          </RequireStaff>
        }
      />
      <Route
        path="/admin/games"
        element={
          <RequireOwner>
            <AdminGames />
          </RequireOwner>
        }
      />
      <Route
        path="/admin/games/:gameId"
        element={
          <RequireOwner>
            <AdminGameDetail />
          </RequireOwner>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <GameList />
          </RequireAuth>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <RequireAuth>
            <Leaderboard />
          </RequireAuth>
        }
      />
      <Route
        path="/game/:gameId"
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="zuhause" replace />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="map" element={<NetworkMap />} />
        <Route path="diagnostics" element={<Diagnostics />} />
        <Route path="offers" element={<Offers />} />
        <Route path="parts" element={<Parts />} />

        <Route path="customers" element={<Customers />} />
        <Route path="products" element={<Products />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="finance" element={<Finance />} />

        <Route path="zuhause" element={<Zuhause />} />
        <Route path="vor-ort" element={<VorOrt />} />
        <Route path="innen/:raum" element={<Innenraum />} />
        <Route path="datacenters" element={<Datacenters />} />
        <Route path="servers" element={<Servers />} />
        <Route path="vps" element={<VpsPage />} />
        <Route path="dedicated" element={<Dedicated />} />
        <Route path="migrations" element={<Migrations />} />
        <Route path="staff" element={<StaffPage />} />

        <Route path="routers" element={<Routers />} />
        <Route path="switches" element={<Switches />} />
        <Route path="links" element={<Links />} />
        <Route path="bgp" element={<Bgp />} />
        <Route path="ixp" element={<Peering />} />
        <Route path="ipam" element={<Ipam />} />
        <Route path="loadbalancer" element={<LoadBalancers />} />
        <Route path="services" element={<Services />} />

        <Route path="research" element={<Research />} />
        <Route path="advisor" element={<Advisor />} />
        <Route path="objectives" element={<Objectives />} />
        <Route path="chronicle" element={<Chronicle />} />
        <Route path="chat" element={<Chat />} />
        <Route path="world" element={<WorldPage />} />
        <Route path="news" element={<News />} />
        <Route path="market" element={<Market />} />
        <Route path="shop" element={<Shop />} />
        <Route path="logs" element={<Logs />} />
        <Route path="help" element={<Help />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
